<template>
  <div>
    <Breadcrumbs main="Admin" title="Admin Users" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-xl-4">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Register Admin</h5>
              <span class="text-muted">Create a new platform operator account</span>
            </div>
            <div class="card-body">
              <div v-if="formSuccess" class="alert alert-success" role="alert">
                {{ formSuccess }}
              </div>
              <div v-if="formError" class="alert alert-danger" role="alert">
                {{ formError }}
              </div>
              <form class="theme-form" @submit.prevent="submitForm">
                <div class="form-group form-field mb-2">
                  <label class="form-label">Full name <span class="text-danger">*</span></label>
                  <input
                    v-model="form.fullName"
                    class="form-control"
                    type="text"
                    required
                    placeholder="Platform Admin"
                  >
                </div>
                <div class="form-group form-field mb-2">
                  <label class="form-label">Email <span class="text-danger">*</span></label>
                  <input
                    v-model="form.email"
                    class="form-control"
                    type="email"
                    required
                    placeholder="admin@wakili.com"
                  >
                </div>
                <div class="form-group form-field mb-2">
                  <label class="form-label">Phone</label>
                  <input
                    v-model="form.phoneNo"
                    class="form-control"
                    type="text"
                    placeholder="0700000000"
                  >
                </div>
                <div class="form-group form-field mb-2">
                  <label class="form-label">Password <span class="text-danger">*</span></label>
                  <input
                    v-model="form.password"
                    class="form-control"
                    type="password"
                    required
                    placeholder="********"
                    autocomplete="new-password"
                  >
                </div>
                <button type="submit" class="btn btn-primary w-100" :disabled="saving">
                  {{ saving ? 'Creating...' : 'Create Admin' }}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Platform Admins</h5>
                <span class="text-muted">Central database operators</span>
              </div>
              <button
                class="btn btn-outline-primary btn-sm"
                type="button"
                :disabled="loading"
                @click="loadUsers"
              >
                <vue-feather type="refresh-cw" class="me-1" />
                Refresh
              </button>
            </div>
            <div class="card-body">
              <div v-if="listError" class="alert alert-warning" role="alert">
                {{ listError }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading admin users...
              </div>
              <div v-else-if="users.length === 0" class="text-center py-4 text-muted">
                No admin users found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in users" :key="user.adminUserId">
                      <td>{{ user.adminUserId }}</td>
                      <td>
                        <strong>{{ user.fullName }}</strong>
                        <span
                          v-if="user.adminUserId === currentUser?.adminUserId"
                          class="badge badge-light-primary ms-1"
                        >
                          You
                        </span>
                      </td>
                      <td>{{ user.email }}</td>
                      <td>{{ user.phoneNo || '—' }}</td>
                      <td>
                        <span
                          class="badge"
                          :class="user.active ? 'badge-success' : 'badge-secondary'"
                        >
                          {{ user.active ? 'Active' : 'Inactive' }}
                        </span>
                      </td>
                      <td>{{ formatDate(user.addedOn) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { adminRegister, listAdminUsers } from '@/services/adminAuthService'

const emptyForm = () => ({
  fullName: '',
  email: '',
  phoneNo: '',
  password: '',
})

function userKey(user) {
  return user?.adminUserId ?? user?.email
}

function mergeUsers(existing, incoming) {
  const map = new Map()
  ;[...existing, ...incoming].forEach((user) => {
    if (user) map.set(userKey(user), user)
  })
  return Array.from(map.values()).sort((a, b) => (b.adminUserId || 0) - (a.adminUserId || 0))
}

export default {
  computed: {
    ...mapGetters('adminAuth', ['currentUser']),
  },

  data() {
    return {
      users: [],
      loading: false,
      listError: '',
      form: emptyForm(),
      formError: '',
      formSuccess: '',
      saving: false,
    }
  },

  created() {
    this.loadUsers()
  },

  methods: {
    async loadUsers() {
      this.loading = true
      this.listError = ''

      try {
        const data = await listAdminUsers()
        const rows = Array.isArray(data) ? data : data ? [data] : []
        this.users = mergeUsers(rows, this.currentUser ? [this.currentUser] : [])
      } catch (err) {
        this.users = this.currentUser ? [this.currentUser] : []
        if (err.status === 401 || err.status === 404) {
          this.listError = 'Could not load the full admin list. Showing your signed-in account.'
        } else {
          this.listError = err.message || 'Failed to load admin users'
        }
      } finally {
        this.loading = false
      }
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },

    buildPayload() {
      const payload = {
        fullName: this.form.fullName.trim(),
        email: this.form.email.trim(),
        password: this.form.password,
      }
      const phone = this.form.phoneNo?.trim()
      if (phone) payload.phoneNo = phone
      return payload
    },

    async submitForm() {
      this.formError = ''
      this.formSuccess = ''
      this.saving = true

      try {
        const created = await adminRegister(this.buildPayload())
        this.users = mergeUsers(this.users, [created])
        this.form = emptyForm()
        this.formSuccess = `${created.fullName} was registered successfully.`
      } catch (err) {
        this.formError = err.message || 'Registration failed'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
